// Recording API client. Backend: backend/web/Roles.Web/Endpoints/Recordings/.
//
//   POST   /api/recordings                       (RecordingSaveEndpoint)
//      → 200 OperationResponse (no id echo — the FE-minted take id IS the recording id)
//   PATCH  /api/recordings/{id:guid}/name        → 200 OperationResponse
//   POST   /api/recordings/{id:guid}/discard     → 200
//
// A practice runs session-less — nothing is persisted server-side during a take.
// A `Recording` is materialised only when the user Saves a take to their Library
// or requests an AI Review (`POST /api/recordings`, then the upload flow attaches
// the blob). See wiki/feature-practice-persistence.md.
//
// Request/response shapes use the generated `@voicedojo/backend-types/recordings`
// DTOs where available; transport goes through the unified JSON client
// (`api.post`/`api.get`/`api.patch`, throw-based) per wiki/feature-api-client.md.
// Each function resolves the typed DTO on success and throws `ApiError` on
// failure.

import { UiKeys, RecordingId, completeSelfReviewCheck, type SelfReviewCheck } from '@voicedojo/backend-types';
import type {
  Practice,
  PracticeMode,
  SaveReason,
  PracticeContent,
  DeliveredCue,
  LivePitchPanelist,
  OperationResponse,
  RenameRecordingCommand,
  DiscardRecordingCommand,
  KeepRecordingInLibraryCommand,
} from '@voicedojo/backend-types';
import { findRecordingById } from '@voicedojo/backend-types/recordings';
import type { Recording } from '@voicedojo/backend-types/recordings';
import { t } from '$lib/i18n';
import { api } from './client';

/**
 * The practices of the VoiceDojo product (wiki/product-vision.md). The practice
 * (the route) is the single content-shape + cue-family axis; every practice page
 * sends its `Practice` literal so a saved recording can be sent back to the
 * originating page.
 *
 * Re-exported from the generated `@voicedojo/backend-types` enum (the single
 * source of truth) so practice pages can import `Practice` from the recordings
 * client alongside the save / label helpers.
 */
export type { Practice };

/**
 * Body for `POST /api/recordings` — the take ran session-less so the client is
 * the source of truth: it carries the complete take metadata the browser holds.
 * `id` is the FE-minted take guid and IS the new recording stream id — the server
 * never mints one (an empty id is a 400) and never echoes it back. The owning
 * user is taken from the JWT principal, never the body.
 *
 * Not the generated `SaveRecordingCommand` (its `content` is untyped), so the FE
 * builds the request object by hand.
 */
export type SaveRecordingRequest = {
  id: string;
  practice: Practice;
  name: string;
  plannedDurationMinutes: number;
  startedAt: string;
  endedAt: string;
  content: PracticeContent;
  cuePlan: DeliveredCue[];
  reason: SaveReason;
  speakerPassage?: string | null;
  // A Live convo take (wiki/feature-live-convo.md §6.2.5): its AI Review leaves the spoken feedback out.
  mode?: PracticeMode;
  liveConvoModelKey?: string | null;
  coachFeedback?: string | null;
  feedbackStartSeconds?: number | null;
  livePitchRoster?: LivePitchPanelist[];
  seriesId?: string | null;
  /**
   * The HONEST decoded media length in seconds (the single source of truth for
   * duration — wiki/feature-ai-review.md §"Duration & the review floor"), probed
   * on the client at save time. Cards, the review floor, and the credit price all
   * read this; the record-button wall-clock is never sent. Nullable — a
   * cache-less caller may leave it unset.
   */
  durationSeconds?: number | null;
  /**
   * The video-quality tier KEY the take was recorded in (e.g. `"1080p"`). Quality is
   * locked during recording, so this equals the tier the finished take captured at.
   * Optional/nullable — a caller with no known tier leaves it unset.
   */
  recordedQuality?: string | null;
  /**
   * The speaker's own free-text context for this take (Manual rehearsal —
   * wiki/feature-rehearsal.md §"Iteration context"). It lands on
   * `RecordingSaved.OptionalContext` and joins the composed context of every AI
   * Review of the take. Unset in every other flow.
   */
  optionalContext?: string | null;
  /**
   * The Series document revision this take was recorded against (Rehearsal only).
   * The series detail page states it per take, so a comparison across takes says
   * honestly when the talk itself changed in between.
   */
  seriesDocumentRevision?: number | null;
  /**
   * The ordered plan-point indices that actually reached the speaker this take —
   * timeline-fired or tapped — over the plan flattened must-deliver-then-if-
   * there's-time. Every AI Review of the take marks each point cued or not cued
   * from it (wiki/feature-rehearsal.md §"Cues off"). Unset in every other flow.
   */
  deliveredPlanPoints?: number[] | null;
  /**
   * The Series plan the take was actually rehearsed against (Rehearsal only) —
   * the plan SELECTED when the take started, which is the plan
   * `deliveredPlanPoints` indexes into. The server copies that plan's snapshot
   * onto the take (wiki/feature-rehearsal.md §"The plan a take was recorded
   * against"); a plan id the Series no longer holds simply copies nothing.
   */
  seriesPlanId?: string | null;
};

/**
 * Display label for a practice — used on the recording-detail page, the AI Review
 * library source pill, and the Progress page. Resolves via `t()` over the
 * generated `Ui.PracticeLabel.*` keys; an unrecognised value resolves to the key
 * itself, which callers can substitute for the raw practice string.
 */
export function practiceLabel(practice: Practice): string {
  return t(UiKeys.PracticeLabel[practice]);
}

/**
 * The practice page each practice routes back to from the recording-detail page.
 * `Partial` — Standalone has no practice page (it is an upload-only review).
 */
export const PRACTICE_ROUTES = {
  TheInstrument: '/',
  ArchetypePlay: '/archetypes',
  BodyLanguagePlay: '/body-language',
  Storytelling: '/storytelling',
  RecordAndReview: '/rehearsal',
  ActiveListening: '/active-listening',
  Riff: '/word-play',
} as const satisfies Partial<Record<Practice, string>>;

/** The set of concrete practice-page routes {@link PRACTICE_ROUTES} maps to. */
export type PracticeRoute = (typeof PRACTICE_ROUTES)[keyof typeof PRACTICE_ROUTES];

/**
 * The bare Guid of a (possibly `offline:`-prefixed) take id. The recording
 * routes bind `{id:guid}`, so the prefix the local cache uses to mark a
 * browser-only take must be stripped before it goes on the wire.
 */
export function bareRecordingId(id: string): RecordingId {
  return RecordingId(id.startsWith('offline:') ? id.slice('offline:'.length) : id);
}

/**
 * The reviewed recording's honest media length in seconds, fetched owner-scoped
 * via `POST /api/query` (`$type: FindRecordingByIdQuery`). The `/reviews`
 * re-review panel prices its `ReviewQualitySelector` off this (the review read
 * model carries no duration). Resolves `0` when the recording is gone / not the
 * caller's — the selector then shows its own load-error state rather than a
 * guessed cost; the actual charge stays server-authoritative. Throws `ApiError`
 * on transport failure.
 */
export async function getRecordingDurationSeconds(recordingId: string): Promise<number> {
  const id = bareRecordingId(recordingId);
  const recordings = await api.post<Recording[]>('/query', {
    $type: findRecordingById.$type,
    id,
  });
  return recordings[0]?.durationSeconds ?? 0;
}

/**
 * Materialise a `Recording` from browser-supplied take metadata — `POST
 * /api/recordings` (wiki/feature-practice-persistence.md). The first step of
 * Save-to-Library / AI-Review: it creates the recording stream with the
 * FE-minted take id so the upload flow can then attach the blob against it.
 *
 * Tier gate is server-side: a Free user may only `reason: 'Review'` (a 403
 * surfaces otherwise — `SaveRequiresPremium`); a guest cannot reach it (401/403).
 * Idempotent against a duplicate Save / Review of the same take id (the backend
 * no-ops a second creation).
 *
 * Resolves an `OperationResponse` — there is no id echo: the `request.id` the
 * caller minted IS the recording id and is what every subsequent step uses.
 * Throws `ApiError` on failure.
 */
export async function saveRecording(
  request: SaveRecordingRequest,
): Promise<OperationResponse> {
  const id = bareRecordingId(request.id);
  return api.post<OperationResponse>(
    '/recordings',
    {
      ...request,
      id,
    },
    { requiresAuth: true },
  );
}

/**
 * Rename a recording — backs the click-to-edit H1 affordance on the
 * recording-detail + review pages. The URL `{id}` is the canonical stream key;
 * the body repeats it as `{ name, id }` so the bound `RenameRecordingCommand`
 * carries its `id` — the member Wolverine.Marten resolves the aggregate stream
 * from — either way. The validator enforces 1–64 trimmed chars. Throws
 * `ApiError` on failure.
 */
export async function renameRecording(
  recordingId: string,
  name: string,
): Promise<OperationResponse> {
  const id = bareRecordingId(recordingId);
  const command: RenameRecordingCommand = { id, name };
  return api.patch<OperationResponse>(`/recordings/${id}/name`, command, {
    requiresAuth: true,
  });
}

/**
 * Discard a server-backed recording — `POST /api/recordings/{id}/discard`
 * (owner-gated; wiki/feature-my-library.md §"Discard"). The single
 * full-teardown path for any saved take: it credits the recording's size back to
 * the storage quota, deletes the stored recording + thumbnail bytes on a
 * background worker, and evicts the recording from the library. The body repeats the route id
 * as `{ id }` so the bound `DiscardRecordingCommand` carries its `id` — the
 * member the aggregate stream is resolved from — either way.
 *
 * Fire-and-forget: resolves on a bodyless `202 Accepted` once the teardown is
 * queued; the take leaves the list via the Recording push channel, not this
 * response. Throws `ApiError` on failure — notably 404 (already gone) and
 * 403 (non-owner).
 */
export async function discardRecording(recordingId: string): Promise<void> {
  const id = bareRecordingId(recordingId);
  const command: DiscardRecordingCommand = { id };
  await api.post<unknown>(`/recordings/${id}/discard`, command, { requiresAuth: true });
}

/**
 * "Save to library" on an already-uploaded take — the library-keep conversion
 * (`POST /api/recordings/{id}/keep`; wiki/feature-recording-storage.md
 * §"'Save to library' on an already-uploaded take"). The bytes of a plan-retained
 * take are already stored and already counted, so the conversion moves nothing and
 * changes no quota total — it only adds the library claim so the plan's
 * `progress-expired` deletion never reaps the take. It therefore succeeds while the
 * user is OVER quota; that is exactly how a take is rescued.
 *
 * One-way (there is no un-keep — Discard is the removal path), idempotent on an
 * already-kept take, and Premium-only (403 otherwise). Fire-and-forget: the flipped
 * `libraryKept` arrives through the `Recording` read-model push, never an
 * optimistic local flag. Throws `ApiError` on failure.
 */
export async function keepRecordingInLibrary(recordingId: string): Promise<OperationResponse> {
  const id = bareRecordingId(recordingId);
  const command: KeepRecordingInLibraryCommand = { id };
  return api.post<OperationResponse>(`/recordings/${id}/keep`, command, {
    requiresAuth: true,
  });
}

/**
 * Records a finished Visual or Auditory self-review check on a Premium user's
 * plan take (wiki/feature-self-review-checks.md). Fire-and-forget: the done
 * check arrives through the `Recording` read-model push. Throws `ApiError`.
 */
export async function recordSelfReviewCheck(recordingId: string, check: SelfReviewCheck): Promise<void> {
  await api.command(
    completeSelfReviewCheck,
    { id: bareRecordingId(recordingId), check },
    { requiresAuth: true },
  );
}
